/**
 * Visual position editor - child of TransformPanel. Content is #visual inside transform popup.
 * UI logic moved from VisualPositionEditorUI. Init via initWithVisualPositionEditor when opening.
 */
import { Cartesian3, Cartographic, HeadingPitchRoll, Math as CesiumMath } from "cesium";
import { AssetGeoLocation, ConstruktedTransformEditors } from "../types/common";
import { saveGeolocation } from "../construkted_ajax";
import type { VisualPositionEditor } from "../core/VisualPositionEditor";
import { BasePanel } from "./BasePanel";
import type { PanelContainer } from "./BasePanel";

export class VisualPositionEditorPanel extends BasePanel {
    private _container: ReturnType<typeof window.jQuery> | null = null;

    private _editor: VisualPositionEditor | null = null;

    private _predefinedGeolocation = false;

    private _jqLabel!: ReturnType<typeof window.jQuery>;
    private _jqLatitudeDiv!: ReturnType<typeof window.jQuery>;
    private _jqLongitudeDiv!: ReturnType<typeof window.jQuery>;
    private _jqAltitudeDiv!: ReturnType<typeof window.jQuery>;
    private _jqTilesetLatitudeInput!: ReturnType<typeof window.jQuery>;
    private _jqTilesetLatitudeLabel!: ReturnType<typeof window.jQuery>;
    private _jqTilesetLongitudeInput!: ReturnType<typeof window.jQuery>;
    private _jqTilesetLongitudeLabel!: ReturnType<typeof window.jQuery>;
    private _jqTilesetAltitude!: ReturnType<typeof window.jQuery>;
    private _jqPlaceAssetOnTerrain!: ReturnType<typeof window.jQuery>;
    private _jqGeoReferenceButton!: ReturnType<typeof window.jQuery>;
    private _jqEnableTerrainImagery!: ReturnType<typeof window.jQuery>;
    private _jqRotationXInput!: ReturnType<typeof window.jQuery>;
    private _jqRotationYInput!: ReturnType<typeof window.jQuery>;
    private _jqRotationZInput!: ReturnType<typeof window.jQuery>;
    private _jqCalculatedScaleInput!: ReturnType<typeof window.jQuery>;
    private _jqResetScaleButton!: ReturnType<typeof window.jQuery>;
    private _jqClearMeasurementPointsButton!: ReturnType<typeof window.jQuery>;
    private _jqMeasuredDistanceInput!: ReturnType<typeof window.jQuery>;
    private _jqKnownDistanceInput!: ReturnType<typeof window.jQuery>;
    private _jqCalcNewScaleButton!: ReturnType<typeof window.jQuery>;
    private _jqReverseUpDirectionCheckbox!: ReturnType<typeof window.jQuery>;
    private _jqUndergroundCheckBox!: ReturnType<typeof window.jQuery>;
    private _jqSaveAndCloseButton!: ReturnType<typeof window.jQuery>;
    private _jqCancelButton!: ReturnType<typeof window.jQuery>;
    private _jqResetButton!: ReturnType<typeof window.jQuery>;

    getId(): string {
        return "visual-position-editor";
    }

    getContainer(): PanelContainer {
        if (!this._container) {
            this._container = window.jQuery("#visual");
        }

        return this._container;
    }

    /** Called by TransformPanel when opening visual position editor. Sets up event handlers. */
    initWithVisualPositionEditor(editor: VisualPositionEditor): void {
        this._editor = editor;

        const transformEditorViewModel = editor.transformEditor.viewModel as {
            headingPitchRoll: HeadingPitchRoll;
            headingPitchRollChanged: { addEventListener: (cb: (hpr: HeadingPitchRoll) => void) => unknown };
            scale: Cartesian3;
            scaleChanged: { addEventListener: (cb: (scale: Cartesian3) => void) => unknown };
        };
        const jQuery = window.jQuery;
        const CONSTRUKTED_AJAX = window.CONSTRUKTED_AJAX;

        this._jqLabel = jQuery("#visual-geolocation-editor-label");
        this._jqLatitudeDiv = jQuery("#latitude-div");
        this._jqLongitudeDiv = jQuery("#longitude-div");
        this._jqAltitudeDiv = jQuery("#altitude-div");
        this._jqTilesetLatitudeInput = jQuery("#tileset_latitude");
        this._jqTilesetLongitudeInput = jQuery("#tileset_longitude");
        this._jqTilesetLatitudeLabel = jQuery("#tileset_latitude_label");
        this._jqTilesetLongitudeLabel = jQuery("#tileset_longitude_label");
        this._jqTilesetAltitude = jQuery("#tileset_altitude");
        this._jqPlaceAssetOnTerrain = jQuery("#place-asset-on-terrain-button");
        this._jqGeoReferenceButton = jQuery("#elevation-reference-button");
        this._jqEnableTerrainImagery = jQuery("#enable-terrain-imagery-visual");
        this._jqRotationXInput = jQuery("#rotation-x");
        this._jqRotationYInput = jQuery("#rotation-y");
        this._jqRotationZInput = jQuery("#rotation-z");
        this._jqCalculatedScaleInput = jQuery("#calculated-scale");
        this._jqResetScaleButton = jQuery("#reset-scale");
        this._jqClearMeasurementPointsButton = jQuery("#clear-measurement-points");
        this._jqMeasuredDistanceInput = jQuery("#measured-distance");
        this._jqKnownDistanceInput = jQuery("#known-distance");
        this._jqCalcNewScaleButton = jQuery("#calc-new-scale-button");
        this._jqUndergroundCheckBox = jQuery("#is-underground-checkbox");
        this._jqSaveAndCloseButton = jQuery("#save_tileset_model_matrix_button");
        this._jqCancelButton = jQuery("#visual-geo-location-cancel-button");
        this._jqResetButton = jQuery("#geo-location-reset-button");
        this._jqReverseUpDirectionCheckbox = jQuery("#reverse-up-direction");

        this._jqEnableTerrainImagery.prop("checked", CONSTRUKTED_AJAX.terrain_imagery_enabled);
        this._showHideTerrainImageryRelativeControls(CONSTRUKTED_AJAX.terrain_imagery_enabled);

        const checkLongitudeLatitude = () => {
            const longitude = parseFloat(this._jqTilesetLongitudeInput.val() as string);
            const latitude = parseFloat(this._jqTilesetLatitudeInput.val() as string);

            if (longitude === 0 && latitude === 0) {
                alert("Please input valid Latitude and Longitude!");

                return false;
            }

            if (Number.isNaN(longitude)) {
                alert("Please input valid Longitude!");

                return false;
            }

            if (Number.isNaN(latitude)) {
                alert("Please input valid Latitude!");

                return false;
            }

            return true;
        };

        this._jqTilesetLongitudeInput.off("change").on("change", () => {
            const longitude = parseFloat(this._jqTilesetLongitudeInput.val() as string);

            if (Number.isNaN(longitude) || longitude > 180 || longitude < -180) {
                this._jqTilesetLongitudeInput.val("");
                alert(`invalid longitude: ${longitude}`);

                return;
            }

            const assetViewer = editor.assetViewer;
            const asset = assetViewer.masterAsset;

            asset.setOneOfBLH(longitude, undefined, undefined);
            assetViewer.groundPlane.longitude = longitude;
            window.Construkted.flyToAssetConditionally();
        });

        this._jqTilesetLatitudeInput.off("change").on("change", () => {
            const latitude = parseFloat(this._jqTilesetLatitudeInput.val() as string);

            if (Number.isNaN(latitude) || latitude > 90 || latitude < -90) {
                this._jqTilesetLatitudeInput.val("");
                alert(`invalid latitude: ${latitude}`);

                return;
            }

            const assetViewer = editor.assetViewer;
            const asset = assetViewer.masterAsset;

            asset.setOneOfBLH(undefined, latitude, undefined);
            assetViewer.groundPlane.latitude = latitude;
            window.Construkted.flyToAssetConditionally();
        });

        this._jqTilesetAltitude.off("change").on("change", () => {
            const altitude = parseFloat(this._jqTilesetAltitude.val() as string);

            if (Number.isNaN(altitude) || altitude > 15000 || altitude < -1000) {
                this._jqTilesetAltitude.val("");
                alert(`invalid altitude: ${altitude}`);

                return;
            }

            const assetViewer = editor.assetViewer;
            const asset = assetViewer.masterAsset;

            asset.setOneOfBLH(undefined, undefined, altitude);
            assetViewer.groundPlane.altitude = altitude;
            window.Construkted.flyToAssetConditionally();
        });

        this._jqGeoReferenceButton.off("click").on("click", () => {
            const geolocationRefMouseHandler = editor.geolocationRefMouseHandler;

            if (!geolocationRefMouseHandler.activated) {
                geolocationRefMouseHandler.activate();
            }
        });

        this._jqPlaceAssetOnTerrain.off("click").on("click", () => {
            if (!checkLongitudeLatitude()) {
                return;
            }

            const longitude = parseFloat(this._jqTilesetLongitudeInput.val() as string);
            const latitude = parseFloat(this._jqTilesetLatitudeInput.val() as string);
            const globe = editor.assetViewer.cesiumViewer.scene.globe;
            const cartographic = new Cartographic(CesiumMath.toRadians(longitude), CesiumMath.toRadians(latitude));
            const terrainHeight = globe.getHeight(cartographic);

            if (!terrainHeight) {
                alert("failed to get height!");

                return;
            }

            const asset = editor.assetViewer.masterAsset;

            asset.placeOnTerrain(longitude, latitude, terrainHeight);
            window.Construkted.flyToAssetConditionally();

            if (asset.hasValidGeoRef()) {
                this._jqTilesetAltitude.val(`${terrainHeight?.toFixed(3)}`);
            } else {
                this._jqTilesetAltitude.val(`${asset.height().toFixed(3)}`);
            }
        });

        this._jqEnableTerrainImagery.off("change").on("change", () => {
            const assetViewer = editor.assetViewer;
            const enabled = this._jqEnableTerrainImagery.is(":checked");

            this._showHideTerrainImageryRelativeControls(enabled);
            assetViewer.showHideSceneBackground(enabled);
            editor.showHideTranslationControl(enabled);
            assetViewer.groundPlane.show = !enabled;
        });

        this._jqSaveAndCloseButton.off("click").on("click", async () => {
            const terrainImageryEnabled = this._jqEnableTerrainImagery.is(":checked");

            if (terrainImageryEnabled && !checkLongitudeLatitude()) {
                return;
            }

            let activeEditor = ConstruktedTransformEditors.VisualPositionEditor;

            if (this._predefinedGeolocation) {
                activeEditor = ConstruktedTransformEditors.PredefinedGeoreference;
            }

            const asset = editor.assetViewer.masterAsset!;
            const saveOptions = {
                activeEditor: activeEditor,
                asset: asset,
                GCPJSONString: undefined,
                isUnderground: this._jqUndergroundCheckBox.prop("checked") as boolean,
                terrainImageryEnabled: terrainImageryEnabled,
                jqControls: [this._jqSaveAndCloseButton]
            };

            await saveGeolocation(saveOptions, () => {
                editor.deactivate({
                    geolocation: asset.calculateGeolocation(),
                    terrainImageryEnabled:
                        activeEditor === ConstruktedTransformEditors.PredefinedGeoreference
                            ? true
                            : terrainImageryEnabled
                });
                window.Construkted.layersPanel.update();
            });
        });

        this._jqCancelButton.off("click").on("click", () => this.cancel());

        this._jqResetButton.off("click").on("click", () => {
            const asset = editor.assetViewer.masterAsset;

            if (asset.originallyGeoreferencedAtECEF) {
                asset.georeference(asset.originalGeolocation());
                editor.assetViewer.cesiumViewer.camera.flyToBoundingSphere(asset.tileset.boundingSphere);
                this.displayGeolocation(asset.originalGeolocation());
            }

            editor.resetRotationAndScale();
        });

        const hpr = transformEditorViewModel.headingPitchRoll;
        const accuracy = 1;

        this._jqRotationXInput.val(CesiumMath.toDegrees(hpr.heading).toFixed(accuracy));
        this._jqRotationYInput.val(CesiumMath.toDegrees(hpr.pitch).toFixed(accuracy));
        this._jqRotationZInput.val(CesiumMath.toDegrees(hpr.roll).toFixed(accuracy));

        transformEditorViewModel.headingPitchRollChanged.addEventListener((headingPitchRoll: HeadingPitchRoll) => {
            this._jqRotationXInput.val(CesiumMath.toDegrees(headingPitchRoll.heading).toFixed(accuracy));
            this._jqRotationYInput.val(CesiumMath.toDegrees(headingPitchRoll.pitch).toFixed(accuracy));
            this._jqRotationZInput.val(CesiumMath.toDegrees(headingPitchRoll.roll).toFixed(accuracy));
        });

        this._jqCalculatedScaleInput.val(transformEditorViewModel.scale.x.toFixed(5));
        transformEditorViewModel.scaleChanged.addEventListener((scale: Cartesian3) => {
            this._jqCalculatedScaleInput.val(scale.x.toFixed(5));
        });

        this._jqClearMeasurementPointsButton.off("click").on("click", () => {
            editor.autoScaleMouseHandler.deactivate();
            editor.autoScaleMouseHandler.activate();
            this._jqMeasuredDistanceInput.val("");
            this._jqKnownDistanceInput.val("");
            jQuery("#scale-known-distance-message").removeClass("hidden");
            this._jqCalcNewScaleButton.removeClass("hidden");
            this._jqCalcNewScaleButton.attr("disabled", "disabled");
        });

        this._jqRotationXInput.off("change").on("change", () => this._onRotationXInputChanged());
        this._jqRotationYInput.off("change").on("change", () => this._onRotationYInputChanged());
        this._jqRotationZInput.off("change").on("change", () => this._onRotationZInputChanged());

        jQuery("#start-auto-orientation")
            .off("click")
            .on("click", () => {
                editor.autoScaleMouseHandler.deactivate();
                editor.autoOrientationMouseHandler.activate();
            });

        jQuery(".transform-modal-back")
            .off("click")
            .on("click", (el: any) => {
                editor.autoScaleMouseHandler.deactivate();
                editor.autoOrientationMouseHandler.deactivate();

                const geolocationRefMouseHandler = editor.geolocationRefMouseHandler;

                if (geolocationRefMouseHandler.activated) {
                    geolocationRefMouseHandler.deactivate();
                }

                jQuery(el.target).parent().toggleClass("hidden");
            });

        this._jqCalculatedScaleInput.off("change").on("change", () => this._onCalculatedScaleInputChanged());

        this._jqResetScaleButton.off("click").on("click", () => {
            this._jqCalculatedScaleInput.val("1.00000");
            this._jqKnownDistanceInput.val(this._jqMeasuredDistanceInput.val());
            this._onCalculatedScaleInputChanged();
        });

        this._jqKnownDistanceInput.off("change input").on("change input", () => this._onKnownDistanceInputChanged());
        this._jqCalcNewScaleButton
            .off("click")
            .on("click", () => (editor as { _onDoScaleMeasurement: () => void })._onDoScaleMeasurement());

        jQuery("#start-auto-scale")
            .off("click")
            .on("click", () => {
                editor.autoOrientationMouseHandler.deactivate();
                editor.autoScaleMouseHandler.activate();
            });

        jQuery(".transform-has-modal-next")
            // .off("click") transform-has-modal-next class is being used on multiple buttons so this line will remove already attached click listeners
            .on("click", (el: any) => {
                jQuery(el.target).next().toggleClass("hidden");

                const assetViewer = editor.assetViewer;
                const asset = assetViewer.masterAsset;

                this._jqCalculatedScaleInput.val(asset?.scale?.x.toFixed(5)).trigger("change");
                this._jqMeasuredDistanceInput.val("").trigger("change");
                this._jqKnownDistanceInput.val("").trigger("change");
                this._jqCalcNewScaleButton.removeClass("hidden");
            });

        this._jqReverseUpDirectionCheckbox.off("click").on("click", () => this._onReverseUpDirectionCheckboxClicked());

        this._jqUndergroundCheckBox.prop("checked", CONSTRUKTED_AJAX.asset_is_underground);
        this._jqUndergroundCheckBox.off("change").on("change", function (this: HTMLInputElement) {
            editor.assetViewer.enableDisableUnderground(this.checked);
        });

        jQuery("#tileset-transparency-slider")
            .off("input change")
            .on("input change", function (this: HTMLInputElement) {
                editor.assetViewer.changeTransparancy(parseFloat(this.value));
            });
    }

    displayBLH(longitude: number, latitude: number, height: number): void {
        const digits = 8;

        this._jqTilesetLongitudeInput.val(longitude.toFixed(digits));
        this._jqTilesetLatitudeInput.val(latitude.toFixed(digits));
        this._jqTilesetAltitude.val(height.toFixed(3));
    }

    displayGeolocation(assetGeoLocation: AssetGeoLocation): void {
        const digits = 8;

        this._jqTilesetLatitudeInput.val(assetGeoLocation.latitude.toFixed(digits));
        this._jqTilesetLongitudeInput.val(assetGeoLocation.longitude.toFixed(digits));
        this._jqTilesetLatitudeLabel.text(assetGeoLocation.latitude.toFixed(digits));
        this._jqTilesetLongitudeLabel.text(assetGeoLocation.longitude.toFixed(digits));
        this._jqTilesetAltitude.val(assetGeoLocation.height.toFixed(3));

        const accuracy = 1;

        this._jqRotationXInput.val(assetGeoLocation.heading.toFixed(accuracy));
        this._jqRotationYInput.val(assetGeoLocation.pitch.toFixed(accuracy));
        this._jqRotationZInput.val(assetGeoLocation.roll.toFixed(accuracy));
    }

    displayHeight(height: number): void {
        this._jqTilesetAltitude.val(height.toFixed(3));
    }

    setPredefinedGeoLocationMode(val: boolean): void {
        this._predefinedGeolocation = val;

        const jQuery = window.jQuery;
        const jqRotationContainer = jQuery("#rotation-container");
        const jqWizardContainer = jQuery("#wizard-container");

        if (val) {
            this._jqLabel.text("Pre-Defined Geo-Reference Editor");
            this._jqGeoReferenceButton.text("Elevation Reference");
            this._jqTilesetLatitudeInput.hide();
            this._jqTilesetLongitudeInput.hide();
            this._jqTilesetLatitudeLabel.show();
            this._jqTilesetLongitudeLabel.show();
            jqRotationContainer.hide();
            jqWizardContainer.hide();
            this._jqEnableTerrainImagery.closest(".annotation-line-item").hide();
            jQuery("#geo-location-reference-description").text(
                "Select a point on the 3D model which will act as the reference for the model's Elevation."
            );
            jQuery("#geo-location-reference-type").text("Elevation Reference");
        } else {
            this._jqLabel.text("Visual Geo-Location Editor");
            this._jqGeoReferenceButton.text("Geo-Location Reference");
            this._jqTilesetLatitudeInput.show();
            this._jqTilesetLongitudeInput.show();
            this._jqTilesetLatitudeLabel.hide();
            this._jqTilesetLongitudeLabel.hide();
            jqRotationContainer.show();
            jqWizardContainer.show();
            this._jqEnableTerrainImagery.closest(".annotation-line-item").show();
            jQuery("#geo-location-reference-description").text(
                "Select a point on the 3D model which will act as the reference for the model's Latitute, Longitude, and Elevation."
            );
            jQuery("#geo-location-reference-type").text("Geo-Location Reference");
        }
    }

    get jqCalculatedScaleInput(): ReturnType<typeof window.jQuery> {
        return this._jqCalculatedScaleInput;
    }

    get jqKnownDistanceInput(): ReturnType<typeof window.jQuery> {
        return this._jqKnownDistanceInput;
    }

    get jqMeasuredDistanceInput(): ReturnType<typeof window.jQuery> {
        return this._jqMeasuredDistanceInput;
    }

    get jqScaleWizardApplyButton(): ReturnType<typeof window.jQuery> {
        return this._jqCalcNewScaleButton;
    }

    get jqReverseUpDirectionCheckbox(): ReturnType<typeof window.jQuery> {
        return this._jqReverseUpDirectionCheckbox;
    }

    _showHideTerrainImageryRelativeControls(enabled: boolean): void {
        if (enabled) {
            this._jqLongitudeDiv.show();
            this._jqLatitudeDiv.show();
            this._jqAltitudeDiv.show();
            this._jqPlaceAssetOnTerrain.show();
            this._jqGeoReferenceButton.show();
        } else {
            this._jqLongitudeDiv.hide();
            this._jqLatitudeDiv.hide();
            this._jqAltitudeDiv.hide();
            this._jqPlaceAssetOnTerrain.hide();
            this._jqGeoReferenceButton.hide();
        }
    }

    update(): void {
        const CONSTRUKTED_AJAX = window.CONSTRUKTED_AJAX;

        this._jqEnableTerrainImagery.prop("checked", CONSTRUKTED_AJAX.terrain_imagery_enabled);
        this._showHideTerrainImageryRelativeControls(CONSTRUKTED_AJAX.terrain_imagery_enabled);
    }

    private _confirmClose(): boolean {
        return window.confirm("Are you sure you want to close? You will lose all changes");
    }

    private _closeEditor(): void {
        let geolocation: AssetGeoLocation | undefined;
        const asset = window.Construkted.assetViewer.masterAsset;
        const CONSTRUKTED_AJAX = window.CONSTRUKTED_AJAX;

        if (CONSTRUKTED_AJAX.active_editor === ConstruktedTransformEditors.VisualPositionEditor) {
            if (CONSTRUKTED_AJAX.asset_geo_location) {
                geolocation = CONSTRUKTED_AJAX.asset_geo_location;
            }
        }

        if (CONSTRUKTED_AJAX.active_editor === ConstruktedTransformEditors.PredefinedGeoreference) {
            if (CONSTRUKTED_AJAX.asset_geo_location_by_pre) {
                geolocation = CONSTRUKTED_AJAX.asset_geo_location_by_pre;
            } else {
                geolocation = asset.originalGeolocation();
            }
        }

        let terrainImageryEnabled = true;

        if (CONSTRUKTED_AJAX.active_editor === ConstruktedTransformEditors.VisualPositionEditor) {
            terrainImageryEnabled = window.CONSTRUKTED_AJAX.terrain_imagery_enabled;
        }

        if (this._editor) {
            this._editor.deactivate({ geolocation, terrainImageryEnabled });
        }

        this._showHideTerrainImageryRelativeControls(terrainImageryEnabled);
        this._jqEnableTerrainImagery.prop("checked", window.CONSTRUKTED_AJAX.terrain_imagery_enabled);
        window.panelManager?.closePanelInternal(this.getId());
    }

    cancel(): void {
        if (!this._confirmClose()) {
            return;
        }

        this._closeEditor();
    }

    private _onRotationXInputChanged(): void {
        const rotationX = parseFloat(this._jqRotationXInput.val() as string);

        if (Number.isNaN(rotationX)) {
            alert("invalid heading");

            return;
        }

        if (this._editor) {
            (this._editor as { heading: number }).heading = rotationX;
        }
    }

    private _onRotationYInputChanged(): void {
        const rotationY = parseFloat(this._jqRotationYInput.val() as string);

        if (Number.isNaN(rotationY)) {
            alert("invalid pitch");

            return;
        }

        if (this._editor) {
            (this._editor as { pitch: number }).pitch = rotationY;
        }
    }

    private _onRotationZInputChanged(): void {
        const rotationZ = parseFloat(this._jqRotationZInput.val() as string);

        if (Number.isNaN(rotationZ)) {
            alert("invalid roll");

            return;
        }

        if (this._editor) {
            (this._editor as { roll: number }).roll = rotationZ;
        }
    }

    private _onCalculatedScaleInputChanged(): void {
        const scale = parseFloat(this._jqCalculatedScaleInput.val() as string);

        if (!this._editor) {
            return;
        }

        const viewModel = this._editor.transformEditor.viewModel as { scale: Cartesian3 };

        if (Number.isNaN(scale)) {
            this._jqCalculatedScaleInput.val(viewModel.scale.x);
            setTimeout(() => alert("invalid scale"), 100);

            return;
        }

        if (scale < 0.00001) {
            this._jqCalculatedScaleInput.val(viewModel.scale.x);
            setTimeout(() => alert("too small input"), 100);

            return;
        }

        const measuredDistance = parseFloat(this._jqMeasuredDistanceInput.val() as string);

        if (!Number.isNaN(measuredDistance)) {
            this._jqKnownDistanceInput.val((measuredDistance * scale).toFixed(5));
        }

        viewModel.scale = new Cartesian3(scale, scale, scale);
    }

    private _onKnownDistanceInputChanged(): void {
        const distance = parseFloat(this._jqKnownDistanceInput.val() as string);

        if (distance > 0) {
            this._jqCalcNewScaleButton.removeAttr("disabled");
            this._jqCalcNewScaleButton.removeClass("hidden");
            window.jQuery("#scale-known-distance-message").addClass("hidden");
        } else {
            this._jqCalcNewScaleButton.attr("disabled", "disabled");
            this._jqCalcNewScaleButton.addClass("hidden");
            window.jQuery("#scale-known-distance-message").removeClass("hidden");
        }
    }

    private _onReverseUpDirectionCheckboxClicked(): void {
        if (!this._editor) {
            return;
        }

        const viewModel = this._editor.transformEditor.viewModel as { headingPitchRoll: HeadingPitchRoll };
        const headingPitchRoll = viewModel.headingPitchRoll;
        const newHeadingPitchRoll = new HeadingPitchRoll();
        let newPitch = headingPitchRoll.pitch + CesiumMath.PI;

        if (newPitch > CesiumMath.TWO_PI) {
            newPitch -= CesiumMath.TWO_PI;
        }

        newHeadingPitchRoll.heading = headingPitchRoll.heading;
        newHeadingPitchRoll.pitch = newPitch;
        newHeadingPitchRoll.roll = headingPitchRoll.roll;
        viewModel.headingPitchRoll = newHeadingPitchRoll;
    }

    show(): void {
        const $ = window.jQuery;

        $("#transform-container").hide();
        $("#globe-status-display").hide();
        $("#multi-gcps").removeClass("active");
        $("#visual").addClass("active");
        this._opened = true;
        this.onShow();
    }

    hide(): void {
        window.jQuery("#visual").removeClass("active");
        this._opened = false;
        this.onHide();
    }

    hasUnsavedChanges(): boolean {
        return this.isOpen();
    }

    confirmDiscardChanges(): boolean {
        const ok = this._confirmClose();

        if (ok) {
            this._closeEditor();
        }

        return ok;
    }
}
